import { FC } from "react";
import { Box, Grid, Typography } from "@mui/material";

export const formatNumber = (value: number): string =>
  (value ?? 0).toLocaleString();

export const formatCurrency = (value: number): string =>
  (value ?? 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    // REV-1819 - do not use decimals (round to nearest dollar)
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export interface Metric {
  label: string;
  value: string;
  // Optional emphasis (e.g. Net Value going negative renders red).
  tone?: "default" | "positive" | "negative";
}

const toneColor = (tone?: Metric["tone"]): string | undefined => {
  if (tone === "positive") return "success.main";
  if (tone === "negative") return "error.main";
  return undefined;
};

export const MetricTile: FC<{ metric: Metric }> = ({ metric }) => (
  <Box
    sx={{
      px: 1,
      py: 0.5,
      borderRadius: 1,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.default",
      height: "100%",
      minWidth: 0,
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      display="block"
      // Reserve two lines for every label so all tiles are the same height
      // regardless of whether a label wraps.
      sx={{ lineHeight: 1.2, minHeight: "2.4em" }}
    >
      {metric.label}
    </Typography>
    <Typography
      variant="subtitle1"
      fontWeight={700}
      sx={{
        lineHeight: 1.3,
        color: toneColor(metric.tone),
        fontVariantNumeric: "tabular-nums",
        overflowWrap: "break-word",
      }}
    >
      {metric.value}
    </Typography>
  </Box>
);

export const SectionLabel: FC<{ children: React.ReactNode; sx?: object }> = ({
  children,
  sx,
}) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{
      display: "block",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      fontWeight: 600,
      mb: 0.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

export const MetricGrid: FC<{ metrics: Metric[]; columns?: number }> = ({
  metrics,
  columns = 2,
}) => (
  <Grid container spacing={1} sx={{ minWidth: 0 }}>
    {metrics.map((metric) => (
      <Grid key={metric.label} size={{ xs: 12 / columns }} sx={{ minWidth: 0 }}>
        <MetricTile metric={metric} />
      </Grid>
    ))}
  </Grid>
);
